// iter120 — Page "Facturation groupée OR Parc"
// Liste tous les OR Parc terminés non facturés, groupés par client. Permet de sélectionner
// plusieurs OR du même client et de générer une facture unique.
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileText, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

function CHF(n) {
  return new Intl.NumberFormat("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
}

export default function OrParcFacturation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState({}); // { client_id: { client_nom, ors: [] } }
  const [selectedOrIds, setSelectedOrIds] = useState(new Set());
  const [selectedClient, setSelectedClient] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/or-parc/billable").then(({ data }) => {
      const g = {};
      for (const r of (data.or_parc || [])) {
        const cid = r.client_id;
        if (!g[cid]) g[cid] = { client_id: cid, client_nom: r.client_nom, ors: [] };
        g[cid].ors.push(r);
      }
      setGroups(g);
    }).catch((e) => toast.error(e.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleOr = (clientId, orId) => {
    setSelectedOrIds((prev) => {
      const next = new Set(prev);
      // Force same-client selection
      if (selectedClient && selectedClient !== clientId) {
        next.clear();
        setSelectedClient(clientId);
      } else if (!selectedClient) {
        setSelectedClient(clientId);
      }
      if (next.has(orId)) next.delete(orId);
      else next.add(orId);
      if (next.size === 0) setSelectedClient("");
      return next;
    });
  };

  const selectAllForClient = (clientId) => {
    const allIds = (groups[clientId]?.ors || []).map((r) => r.id);
    setSelectedClient(clientId);
    setSelectedOrIds(new Set(allIds));
  };

  const selectionTotal = useMemo(() => {
    if (!selectedClient) return 0;
    return (groups[selectedClient]?.ors || [])
      .filter((r) => selectedOrIds.has(r.id))
      .reduce((s, r) => s + (r.total_ht || 0), 0);
  }, [selectedOrIds, selectedClient, groups]);

  const handleGroup = async () => {
    if (!selectedClient || selectedOrIds.size === 0) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/or-parc/facture-grouper", {
        client_id: selectedClient,
        or_ids: Array.from(selectedOrIds),
      });
      toast.success(`Facture ${data.numero} créée — ${data.n_or_inclus} OR · CHF ${CHF(data.total_ttc)}`);
      setSelectedOrIds(new Set());
      setSelectedClient("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur facturation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-10 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#52a675]" /></div>;

  const clientIds = Object.keys(groups);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid="or-parc-facturation-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/atelier")} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Facturation groupée OR Parc
          </h1>
          <p className="text-xs text-gray-500">Sélectionne les OR terminés à facturer en une seule facture par client.</p>
        </div>
      </div>

      {clientIds.length === 0 && (
        <Card className="p-10 text-center border-dashed border-2 border-gray-200">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="font-semibold text-gray-700">Aucun OR Parc à facturer</h3>
          <p className="text-sm text-gray-500 mt-1">Les OR Parc terminés non encore facturés apparaîtront ici.</p>
        </Card>
      )}

      {clientIds.map((cid) => {
        const g = groups[cid];
        const disabled = selectedClient && selectedClient !== cid;
        return (
          <Card key={cid} className={`p-5 space-y-3 ${disabled ? "opacity-40" : ""}`}>
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">{g.client_nom}</h2>
              <Button size="sm" variant="outline" onClick={() => selectAllForClient(cid)} disabled={disabled} data-testid={`or-parc-select-all-${cid}`}>
                Tout sélectionner
              </Button>
            </div>
            <div className="space-y-2">
              {g.ors.map((r) => {
                const checked = selectedOrIds.has(r.id);
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => toggleOr(cid, r.id)}
                    disabled={disabled}
                    data-testid={`or-parc-row-${r.numero}`}
                    className={`w-full text-left flex items-center gap-3 p-3 border-2 rounded-lg transition ${
                      checked ? "border-[#52a675] bg-[#52a675]/10" : "border-gray-200 hover:border-gray-300"
                    } ${disabled ? "cursor-not-allowed" : ""}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${checked ? "bg-[#52a675] border-[#52a675]" : "border-gray-300"}`}>
                      {checked && <Check className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">OR {r.numero} · {r.vehicule_label}</div>
                      <div className="text-[11px] text-gray-500">{r.lignes?.length || 0} lignes · statut <code>{r.statut}</code></div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">CHF {CHF(r.total_ht)}</div>
                      <div className="text-[10px] text-gray-400">HT</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        );
      })}

      {selectedOrIds.size > 0 && (
        <div className="sticky bottom-4 bg-white/90 backdrop-blur border border-gray-200 rounded-2xl p-4 flex items-center justify-between shadow-lg" data-testid="or-parc-action-bar">
          <div className="text-sm text-gray-700">
            <b>{selectedOrIds.size}</b> OR sélectionné(s) · Total HT : <b>CHF {CHF(selectionTotal)}</b>
          </div>
          <Button
            onClick={handleGroup}
            disabled={submitting}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="or-parc-facture-grouper"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            Créer la facture groupée
          </Button>
        </div>
      )}
    </div>
  );
}
